import { Component, Vue } from 'vue-property-decorator'
import AppDatatable from '@core/components/AppDatatable.vue'
import CommissionRecord from '../model/CommissionRecord'
import { FormStructure } from '@master/model/structure/FormStructure'
import AppForm from '@core/components/AppForm.vue'
import { capitalize, isEmpty, isUndefined } from 'lodash'
import { inject } from 'inversify-props'
import API from '@core/services/API'
import { IDateService } from '@master/services/IDateService'
import { CommissionBatchDTO, CommissionData, CommissionRecordDTO, CommissionRecordsService } from '../api'
import { TransactionCsvModel } from './csv-model/TransactionCsvModel'
import { ManulifeTransactionCsvModel } from './csv-model/ManulifeTransactionCsvModel'
import { NTUCTransactionCsvModel } from './csv-model/NTUCTransactionCsvModel'
import { AvivaTransactionCsvModel } from './csv-model/AvivaTransactionCsvModel'
import { AXATransactionCsvModel } from './csv-model/AXATransactionCsvModel'
import { NavCommTransactionCsvModel } from './csv-model/NavCommTransactionCsvModel'
import { NavWrapTransactionCsvModel } from './csv-model/NavWrapTransactionCsvModel'
import { IFASTCommTransactionCsvModel } from './csv-model/IFASTCommTransactionCsvModel'
import { IFASTWrapTransactionCsvModel } from './csv-model/IFASTWrapTransactionCsvModel'
import { IFASTTrailerTransactionCsvModel } from './csv-model/IFASTTrailerTransactionCsvModel'
import { TokioTransactionCsvModel } from './csv-model/TokioTransactionCsvModel'
import CSVImport from '@core/services/csv/CSVImport.vue'
import AppDialog from '@core/components/AppDialog.vue'
import { SharedVariable } from '@core/model/SharedVariable'
import { UIService } from '@core/services/UIService'
import { IProjectSettings } from '@master/config/IProjectSettings'
/* eslint-disable @typescript-eslint/camelcase */
@Component({
  components: {
    AppDatatable,
    AppForm,
    CSVImport,
    AppDialog
  }
})
export default class TransactionMixin extends Vue {
  @inject()
  private $api: API

  @inject()
  protected dateService: IDateService

  @inject()
  protected sharedVariable: SharedVariable

  @inject()
  private projectSettings: IProjectSettings

  protected uiService: UIService
  private debug = true
  loadingTransactions = false
  private loadingTransactionRecords = false
  txtSearchTop = ''
  txtSearchTopRecord = ''
  showImportCsvRecords = false
  modalPage: 1 | 2 = 1
  private principalList: Array<string> = []
  newTransaction = new CommissionRecord()
  private commissionRecords: CommissionBatchDTO[] = []
  private importedCommissionRecords: CommissionRecordDTO[] = []
  csvErrors: Array<string[]> = []
  isImporting = false
  private remunerationTypeMap = [
    'Commission',
    'Trailer Fee',
    'Wrap Fee'
  ]

  enableIssuerTemplate = false
  issuerTemplate = 'IFAM Template'

  transactionModel = null

  setTransactionModel () {
    this.transactionModel = TransactionCsvModel
  }

  get issuerTemplateAvailable () {
    const CsvModel = this.getIssuerCsvTemplate(this.newTransaction.principal, this.newTransaction.remunerationType)
    return CsvModel !== this.transactionModel
  }

  get commissionRecordsData () {
    return this.commissionRecords.map(rec => {
      return {
        paymentDateMillis: this.dateService.format(parseInt(rec.paymentDateMillis), 'YYYY-MM-DD'),
        statementDateMillis: this.dateService.format(parseInt(rec.statementDateMillis), 'YYYY-MM-DD'),
        paid: capitalize(rec.paid),
        principal: rec.principal,
        remunerationType: this.remunerationTypeMap[rec.remunerationType],
        records: rec.count,
        taxInvoiceNo: rec.taxInvoiceNo,
        redirect: `/live/commission-details?_oid=${rec.commissionBatchOid}`
      } as any
    })
  }

  get commissionRecordsColumn () {
    return [
      {
        label: 'Payment Date',
        field: 'paymentDateMillis'
      },
      {
        label: 'Statement Date',
        field: 'statementDateMillis'
      },
      {
        label: 'Paid Status',
        field: 'paid'
      },
      { label: 'Principal', field: 'principal' },
      { label: 'Remuneration Type', field: 'remunerationType' },
      { label: 'Tax Invoice No.', field: 'taxInvoiceNo' },
      { label: 'Records', field: 'records' }
    ]
  }

  get importedCommissionRecordsColumn (): Array<{ label: string; field: keyof CommissionRecordDTO }> {
    return [
      { label: 'Adviser Code', field: 'advCode' },
      { label: 'Adviser Name', field: 'advName' },
      { label: 'Policy Number', field: 'policyNumber' },
      { label: 'Client Name', field: 'clientName' },
      { label: 'Product Name', field: 'productName' },
      { label: 'Premium', field: 'premiumAmt' },
      { label: 'Commission', field: 'commissionAmt' },
      { label: 'OR Commission', field: 'orCommissionAmt' },
      { label: 'Transaction Type', field: 'transactionType' },
      { label: 'Remark', field: 'remark' }
    ]
  }

  get importedCommissionRecordsData () {
    return this.importedCommissionRecords
  }

  get newTransactionFormStructure (): FormStructure<CommissionRecord> {
    return [
      {
        type: 'date',
        label: 'Payment Date*',
        key: 'paymentDateMillis'
      },
      {
        type: 'date',
        label: 'Statement Date*',
        key: 'statementDateMillis'
      },
      {
        label: 'Principal*',
        key: 'principal',
        options: this.principalList.map(principal => {
          return {
            label: principal,
            value: principal
          }
        })
      },
      {
        label: 'Remuneration Type*',
        key: 'remunerationType',
        options: [
          { label: '', value: undefined },
          { label: 'Commission', value: 0 },
          { label: 'Trailer Fee', value: 1 },
          { label: 'Wrap Fee', value: 2 }
        ]
      },
      {
        label: 'Paid*',
        key: 'paid',
        options: [
          { label: 'Paid', value: 'paid' },
          { label: 'Unpaid', value: 'unpaid' }
        ]
      },
      {
        label: 'Tax Invoice No.*',
        key: 'taxInvoiceNo'
      },
      {
        label: 'Remarks',
        key: 'batchRemark'
      }
    ]
  }

  async fetchCommissionRecords () {
    this.commissionRecords = await CommissionRecordsService.commissionRecords()
  }

  async fetchPrincipalList () {
    if (isEmpty(this.principalList)) {
      const headers = ['company_details_name']
      const res = await this.$api.companyDetails.list({
        headers: JSON.stringify(headers)
      })
      this.principalList = res.data.map(e => {
        return e[0]
      }).sort()
    }
  }

  get csvModel () {
    if (!this.enableIssuerTemplate || this.issuerTemplate === 'IFAM Template' || !this.issuerTemplateAvailable) {
      return this.transactionModel
    }

    return this.getIssuerCsvTemplate(this.newTransaction.principal, this.newTransaction.remunerationType)
  }

  getIssuerCsvTemplate (issuer: string, remunerationType: number) {
    const issuerName = issuer.toLowerCase()

    if (issuerName.includes('manulife')) {
      return ManulifeTransactionCsvModel
    }

    if (issuerName === 'aviva' || issuerName === 'singlife with aviva') {
      return AvivaTransactionCsvModel
    }

    if (issuerName.includes('axa') || issuerName.includes('hsbc')) {
      return AXATransactionCsvModel
    }

    if (issuerName.includes('ntuc')) {
      return NTUCTransactionCsvModel
    }

    if (issuerName.includes('axa')) {
      return AXATransactionCsvModel
    }

    if (issuerName.includes('tokio')) {
      return TokioTransactionCsvModel
    }

    if (issuerName.includes('ifast')) {
      switch (remunerationType) {
        case 0:
          return IFASTCommTransactionCsvModel
        case 1:
          return IFASTTrailerTransactionCsvModel
        case 2:
          return IFASTWrapTransactionCsvModel
      }
    }

    if (issuerName.includes('nav')) {
      switch (remunerationType) {
        case 0:
          return NavCommTransactionCsvModel
        case 2:
          return NavWrapTransactionCsvModel
      }
    }

    if (issuerName.includes('tokio')) {
      switch (remunerationType) {
        case 0:
          return NavCommTransactionCsvModel
        case 2:
          return NavWrapTransactionCsvModel
      }
    }

    return this.transactionModel
  }

  async goToPage2 () {
    const incomplete =
      (isUndefined(this.newTransaction.paymentDateMillis) ||
      isUndefined(this.newTransaction.statementDateMillis) ||
      isEmpty(this.newTransaction.principal) ||
      isEmpty(this.newTransaction.paid) ||
      isEmpty(this.newTransaction.taxInvoiceNo) ||
      isUndefined(this.newTransaction.remunerationType))

    if (incomplete) {
      alert('Please fill in all required input')
      return
    }
    try {
      await this.validateTaxInvoiceNo()
      this.modalPage = 2

      if (this.issuerTemplateAvailable) {
        this.issuerTemplate = 'Issuer Template'
      }
    } catch (error) {
      await this.uiService.alert.show({
        type: 'error',
        message: error
      })
    }
  }

  closeModalCsv () {
    this.showImportCsvRecords = false
    this.modalPage = 1
  }

  toggleImportCsvRecords () {
    this.showImportCsvRecords = true
  }

  async onCommissionRecordCsvUpload (records: TransactionCsvModel[]) {
    const id = this.uiService.randomID()
    this.uiService.loading.addLoadingTask(id)
    try {
      const imported: CommissionData[] = records.map(({ advCode, advName, policyNumber, clientName, productName, productType, premiumAmt, commissionAmt, orCommissionAmt, issuedDate, paidToDate, payMode }) => {
        return { advCode, advName, policyNumber, clientName, productName, productType, premiumAmt, commissionAmt, orCommissionAmt, issuedDate, paidToDate, payMode }
      })
      this.csvErrors = await CommissionRecordsService.validateNewCommissionData(imported)
    } finally {
      this.uiService.loading.removeLoadingTask(id)
    }
  }

  async importCsv (records: TransactionCsvModel[]) {
    const id = this.uiService.randomID()
    this.uiService.loading.addLoadingTask(id)
    try {
      this.isImporting = true
      const { paymentDateMillis, statementDateMillis, paid, principal, remunerationType, taxInvoiceNo, batchRemark } = this.newTransaction
      const imported: CommissionData[] = records.map(({ advCode, advName, policyNumber, clientName, productName, productType, premiumAmt, commissionAmt, orCommissionAmt, issuedDate, paidToDate, payMode }) => {
        return { advCode, advName, policyNumber, clientName, productName, productType, premiumAmt, commissionAmt, orCommissionAmt, issuedDate, paidToDate, payMode }
      })
      const _oid = this.projectSettings.useHierarchySetup ? await CommissionRecordsService.importNewCommissionDataViaHierarchySetup({
        paymentDate: paymentDateMillis,
        statementDate: statementDateMillis,
        paid,
        principal,
        remunerationType,
        taxInvoiceNo,
        remark: batchRemark,
        commissionData: imported
      })
        : await CommissionRecordsService.importNewCommissionData({
          paymentDate: paymentDateMillis,
          statementDate: statementDateMillis,
          paid,
          principal,
          remunerationType,
          taxInvoiceNo,
          remark: batchRemark,
          commissionData: imported
        })

      this.$router.push({ name: 'Commission Details', query: { _oid } })
    } finally {
      this.isImporting = false
      this.uiService.loading.removeLoadingTask(id)
    }
  }

  async validateTaxInvoiceNo () {
    await CommissionRecordsService.validateTaxInvoiceNo({ taxInvoiceNo: this.newTransaction.taxInvoiceNo })
  }

  async mounted () {
    this.uiService = this.sharedVariable.uiService
    await this.fetchCommissionRecords()
    await this.fetchPrincipalList()
    const { commission_template } = await this.$api.system.information()
    this.enableIssuerTemplate = commission_template ?? false
    this.setTransactionModel()
  }
}
